import {isDefined} from '../../../../utils';

// Bilgi doğruluk oranı: sitenin ne kadarinin doldurulmus oldugunu tek sayiya indirger.
// Kaynak, EditWeb ekraninin cektigi get-edit-content yanitidir; blog / SSS / popup gibi
// ayri servislerden gelen sayilar disaridan `extras` ile eklenir.
//
// Her alan 0 (eksik) / 0.5 (kismi) / 1 (tam) puan alir; oran bu puanlarin ortalamasidir.
// `optional: true` isaretli alanlar hesaba hic girmez (ne paya ne paydaya): sitede
// zorunlu olmayan, alternatifi bulunan alanlardir — orn. kapak videosu, yerine kapak
// fotografi gosterilir. Listede bilgi olarak durur ama eksik sayilmaz.

const DONE = 'done';
const PARTIAL = 'partial';
const MISSING = 'missing';

const WEIGHT = {[DONE]: 1, [PARTIAL]: 0.5, [MISSING]: 0};

const safeArray = (value) => (Array.isArray(value) ? value : []);

export function buildHealthChecks(editWebData, extras = {}) {
    const data = editWebData && editWebData.length !== 0 ? editWebData : {};

    const photos = safeArray(data.property_web_photos);
    const galleryPhotos = photos.filter((photo) => photo.is_selected && !photo.is_cover);
    const coverPhotos = photos.filter((photo) => photo.is_cover === true);

    const selectedLanguages = safeArray(data.languages).filter((lang) => lang.is_selected === true);
    const languageCount = selectedLanguages.length || 1;

    const aboutUs = safeArray(data.property_web_about_us);
    const filledAboutUs = aboutUs.filter((item) => isDefined(item?.value) && String(item.value).trim() !== '');

    const rooms = safeArray(data.rooms);
    const selectedRooms = rooms.filter((room) => room.is_selected === true);

    const places = safeArray(data.places).filter((place) => place.is_selected === true);
    const menus = safeArray(data.menus).filter((menu) => menu.is_selected === true);

    const colors = safeArray(data.colors).filter((color) => isDefined(color?.color_code) && color.color_code !== '');

    const additional = data.additional_info || {};
    const kvkkFields = [
        additional.kvkk_data_controller,
        additional.kvkk_address,
        additional.kvkk_email,
        additional.kvkk_phone,
    ];
    const filledKvkk = kvkkFields.filter((field) => isDefined(field) && String(field).trim() !== '');

    // extras: ayri servislerden gelen sayilar (blog, sss, popup, meta etiket)
    const blogCount = Number(extras.blogCount) || 0;
    const faqCount = Number(extras.faqCount) || 0;
    const popupCount = Number(extras.popupCount) || 0;

    const ratio = (filled, total) => {
        if (!total) return MISSING;
        if (filled >= total) return DONE;
        return filled > 0 ? PARTIAL : MISSING;
    };

    return [
        {
            key: 'gallery',
            label: 'enw-web-settings-check_gallery',
            fallback: 'Galeri fotoğrafları',
            status: galleryPhotos.length >= 6 ? DONE : galleryPhotos.length > 0 ? PARTIAL : MISSING,
            value: `${galleryPhotos.length}`,
            target: 'photos',
        },
        {
            key: 'cover',
            label: 'enw-web-settings-check_cover',
            fallback: 'Kapak fotoğrafı',
            status: coverPhotos.length > 0 ? DONE : MISSING,
            value: `${coverPhotos.length}`,
            target: 'photos',
        },
        {
            key: 'about',
            label: 'enw-web-settings-check_about',
            fallback: 'Hakkımızda metni',
            status: ratio(filledAboutUs.length, languageCount),
            value: `${filledAboutUs.length}/${languageCount}`,
            target: 'about',
        },
        {
            key: 'rooms',
            label: 'enw-web-settings-check_rooms',
            fallback: 'Oda seçimi',
            status: ratio(selectedRooms.length, rooms.length),
            value: `${selectedRooms.length}/${rooms.length}`,
            target: 'rooms',
        },
        {
            key: 'places',
            label: 'enw-web-settings-check_places',
            fallback: 'Çevre yerleri',
            status: places.length > 0 ? DONE : MISSING,
            value: `${places.length}`,
            target: 'places',
        },
        {
            key: 'menus',
            label: 'enw-web-settings-check_menus',
            fallback: 'Menü seçimi',
            status: menus.length > 0 ? DONE : MISSING,
            value: `${menus.length}`,
            target: 'menus',
        },
        {
            key: 'colors',
            label: 'enw-web-settings-check_colors',
            fallback: 'Site renkleri',
            status: colors.length > 0 ? DONE : MISSING,
            value: colors.length > 0 ? `${colors.length}` : '—',
            target: 'colors',
        },
        {
            key: 'coverVideo',
            label: 'enw-web-settings-check_cover_video',
            fallback: 'Kapak videosu',
            // Kapak fotografinin alternatifi; girilmemesi eksiklik degil
            optional: true,
            status: isDefined(data.cover_video_id) && data.cover_video_id !== '' ? DONE : MISSING,
            value: isDefined(data.cover_video_id) && data.cover_video_id !== '' ? 'Var' : '—',
            target: 'coverVideo',
        },
        {
            key: 'contact',
            label: 'enw-web-settings-check_contact',
            fallback: 'İletişim e-postası',
            status: isDefined(additional.myweb_contact_email) && additional.myweb_contact_email !== '' ? DONE : MISSING,
            value: isDefined(additional.myweb_contact_email) && additional.myweb_contact_email !== '' ? 'Var' : '—',
            target: 'contact',
        },
        {
            key: 'kvkk',
            label: 'enw-web-settings-check_kvkk',
            fallback: 'KVKK bilgileri',
            status: ratio(filledKvkk.length, kvkkFields.length),
            value: `${filledKvkk.length}/${kvkkFields.length}`,
            target: 'kvkk',
        },
        {
            key: 'blog',
            label: 'enw-web-settings-check_blog',
            fallback: 'Blog içeriği',
            status: blogCount > 0 ? DONE : MISSING,
            value: `${blogCount}`,
            target: 'blog',
        },
        {
            key: 'faq',
            label: 'enw-web-settings-check_faq',
            fallback: 'Sıkça sorulan sorular',
            status: faqCount > 0 ? DONE : MISSING,
            value: `${faqCount}`,
            target: 'faq',
        },
        {
            key: 'popup',
            label: 'enw-web-settings-check_popup',
            fallback: 'Popup / kampanya',
            status: popupCount > 0 ? DONE : MISSING,
            value: `${popupCount}`,
            target: 'popup',
        },
    ];
}

// Opsiyonel alanlar skoru ne yukseltir ne dusurur; yalnizca zorunlu alanlar sayilir.
export function calculateHealthScore(checks) {
    const scored = checks.filter((check) => !check.optional);
    if (!scored.length) return 0;
    const total = scored.reduce((sum, check) => sum + (WEIGHT[check.status] || 0), 0);
    return Math.round((total / scored.length) * 100);
}

export const CHECK_STATUS = {DONE, PARTIAL, MISSING};
